/**
 * Metric Explore — shared contract.
 *
 * The Explore surface lets a user assemble up to N parallel metric
 * time-series ("series") on a single chart, each with its own filters.
 * The same Zod shape is shared by:
 *
 *   • dashboard-v2 (URL state codec + React Query hook input)
 *   • apps/api    (POST /v1/metrics/explore request validation)
 *   • clickweave-tools / CLI (auto-generated from OpenAPI)
 *
 * v1 limitation — only metrics the Tinybird `snapshots_events` datasource
 * carries are supported (Web Vitals + timing + 4 Lighthouse scores). Byte
 * weight + execution + opportunity metrics live in `snapshots.summary`
 * but are not yet replicated into Tinybird. Extending the event schema
 * + a runner backfill is a separate chip; until then the explore page
 * surfaces the metric vocabulary below and hides the rest from the
 * metric picker.
 */
import { z } from 'zod';

// ─── Vocabulary ─────────────────────────────────────────────────────────────

/**
 * Metric keys explorable in v1. The string values match column names in
 * the Tinybird `snapshots_events` datasource exactly — the pipe template
 * dispatches on these to pick which column to aggregate.
 *
 * When extending: add the key here, add a column to snapshots_events,
 * add a branch to the explore pipes, run `tb deploy`, regen tools.
 */
export const EXPLORE_METRIC_KEYS = [
  // Web Vitals + timing (ms, except cls)
  'lcp',
  'cls',
  'inp',
  'ttfb',
  'fcp',
  'si',
  'tbt',
  // Lighthouse category scores (0..1)
  'performance_score',
  'accessibility_score',
  'best_practices_score',
  'seo_score',
] as const;

export type ExploreMetricKey = (typeof EXPLORE_METRIC_KEYS)[number];

export const ExploreMetricKeySchema = z.enum(EXPLORE_METRIC_KEYS);

/** Display unit for a metric — drives Y-axis assignment + tooltip format. */
export type MetricUnit = 'ms' | 'score' | 'unitless';

export const METRIC_UNIT: Record<ExploreMetricKey, MetricUnit> = {
  lcp: 'ms',
  cls: 'unitless',
  inp: 'ms',
  ttfb: 'ms',
  fcp: 'ms',
  si: 'ms',
  tbt: 'ms',
  performance_score: 'score',
  accessibility_score: 'score',
  best_practices_score: 'score',
  seo_score: 'score',
};

/** Human label for a metric (matches existing dashboard copy). */
export const METRIC_LABEL: Record<ExploreMetricKey, string> = {
  lcp: 'LCP',
  cls: 'CLS',
  inp: 'INP',
  ttfb: 'TTFB',
  fcp: 'FCP',
  si: 'Speed Index',
  tbt: 'TBT',
  performance_score: 'Performance',
  accessibility_score: 'Accessibility',
  best_practices_score: 'Best Practices',
  seo_score: 'SEO',
};

// ─── Aggregation knobs ──────────────────────────────────────────────────────

export const PERCENTILES = ['p50', 'p75', 'p95', 'p99', 'avg', 'raw'] as const;
export type Percentile = (typeof PERCENTILES)[number];
export const PercentileSchema = z.enum(PERCENTILES);

export const DEVICES = ['mobile', 'desktop'] as const;
/** Device-type enum for the Explore surface; distinct from the `Device` _entity_ in schemas.ts. */
export type DeviceType = (typeof DEVICES)[number];
export const DeviceTypeSchema = z.enum(DEVICES);

/**
 * Bucket — time granularity of one chart point when `percentile != 'raw'`.
 * Ignored when `percentile === 'raw'` (one point per audit instead).
 */
export const BUCKETS = ['5m', '15m', '1h', '6h', '1d', '7d'] as const;
export type Bucket = (typeof BUCKETS)[number];
export const BucketSchema = z.enum(BUCKETS);

/**
 * Lookback window. Enum (not arbitrary string) for URL stability + caching.
 *
 * `365d` was dropped from v1 — a year of audit data isn't an "explore"
 * use case (deeper history goes through audit-detail / report-export
 * paths), and the worst-case Tinybird scan at 365d × all-pages is
 * disproportionately expensive for an interactive surface. Re-add it
 * only after we have org-level scan budgets in place.
 */
export const RANGES = ['1h', '24h', '7d', '30d', '90d'] as const;
export type Range = (typeof RANGES)[number];
export const RangeSchema = z.enum(RANGES);

/**
 * Allow-listed bucket per range. The matrix is the primary cost
 * guardrail at the request shape layer: the UI only offers these
 * combinations, and `validateExploreShape` rejects anything else
 * (URL-paste path can't bypass).
 *
 * Worst-case point counts:
 *
 *   1h  × 5m   = 12   |  1h  × 15m  = 4
 *   24h × 15m  = 96   |  24h × 1h   = 24
 *   7d  × 1h   = 168  |  7d  × 6h   = 28
 *   30d × 6h   = 120  |  30d × 1d   = 30
 *   90d × 1d   = 90   |  90d × 7d   = 13
 *
 * All under 200 points/series — keeps the chart readable AND bounds
 * the worst-case scan. The first bucket in each list is the default
 * picked when the range changes (most granular safe option).
 */
export const ALLOWED_BUCKETS_BY_RANGE: Record<Range, readonly Bucket[]> = {
  '1h': ['5m', '15m'],
  '24h': ['15m', '1h'],
  '7d': ['1h', '6h'],
  '30d': ['6h', '1d'],
  '90d': ['1d', '7d'],
};

/** Most granular safe bucket for the range; used when the range changes. */
export function defaultBucketFor(range: Range): Bucket {
  return ALLOWED_BUCKETS_BY_RANGE[range][0]!;
}

/** True if (range, bucket) is on the allow-list matrix. */
export function isBucketAllowedFor(range: Range, bucket: Bucket): boolean {
  return (ALLOWED_BUCKETS_BY_RANGE[range] as readonly Bucket[]).includes(bucket);
}

/**
 * Visualisation type — the chart shape the explore page renders.
 *
 *   line    — default. Best for trends + multi-series comparison.
 *   area    — like line but filled; emphasizes magnitude over time.
 *   bar     — discrete buckets, easier comparison of individual values.
 *             Cluttered with many series × many buckets.
 *   scatter — disconnected dots; natural fit for `percentile='raw'`
 *             where each point is an audit, not a bucket.
 *
 * Per-chart, not per-series — having different series rendered as
 * different shapes on the same chart is more confusing than useful.
 */
export const VISUALISATIONS = ['line', 'area', 'bar', 'scatter'] as const;
export type Visualisation = (typeof VISUALISATIONS)[number];
export const VisualisationSchema = z.enum(VISUALISATIONS);

/**
 * Pick a sensible visualisation default given the current series shape.
 * Raw mode → scatter (audit-level dots); everything else → line.
 *
 * Used by the dashboard when `viz` is absent from the URL — the user's
 * explicit pick (when present) always wins.
 */
export function defaultVizFor(series: Pick<Series, 'percentile'>[]): Visualisation {
  return series.some((s) => s.percentile === 'raw') ? 'scatter' : 'line';
}

// ─── Series shape ───────────────────────────────────────────────────────────

/**
 * One line on the chart. `id` is client-generated and stable across
 * URL <-> state round-trips so colour assignment + per-series fetch
 * state survive edits.
 */
export const SeriesSchema = z.object({
  /** Client-generated stable id (any non-empty string). */
  id: z.string().min(1).max(32),
  metric: ExploreMetricKeySchema,
  /**
   * Required for v1. The earlier `null = active site from route context`
   * shape from the design doc was dropped together with the active-site
   * concept in #51 — every series carries an explicit site.
   */
  site_uuid: z.string().startsWith('site-'),
  page_uuid: z.string().startsWith('pg-').optional(),
  device: DeviceTypeSchema.optional(),

  /** Region identifier (e.g. `europe-west2`) — exact match against snapshots_events.region. */
  region: z.string().min(1).max(64).optional(),
  percentile: PercentileSchema,
  /** Optional user-supplied display label. Falls back to a derived label server-side. */
  label: z.string().max(120).optional(),
});

export type Series = z.infer<typeof SeriesSchema>;

// ─── Request / response ─────────────────────────────────────────────────────

/**
 * Hard cap on series count per request — the server-side limit.
 *
 * The UI gates at the lower MAX_SERIES_UI_RECOMMENDED below to make
 * accidental fan-out harder; URL-paste / power-user paths can still
 * push up to MAX_SERIES_PER_REQUEST.
 */
export const MAX_SERIES_PER_REQUEST = 10;

/**
 * Friendlier UI cap. The Add / Duplicate buttons disable past this;
 * the series counter shows `n/MAX_SERIES_UI_RECOMMENDED`.
 */
export const MAX_SERIES_UI_RECOMMENDED = 6;

export const ExploreRequestSchema = z.object({
  series: z.array(SeriesSchema).min(1).max(MAX_SERIES_PER_REQUEST),
  range: RangeSchema,
  bucket: BucketSchema,
});

export type ExploreRequest = z.infer<typeof ExploreRequestSchema>;

/** One time-bucketed sample for an aggregated series. */
export const ExplorePointSchema = z.object({
  /** ISO-8601 timestamp at the start of the bucket. */
  ts: z.string(),
  /** Aggregated metric value. `null` when no samples in this bucket. */
  value: z.number().nullable(),
  /** Number of audits aggregated into this bucket (0 when value is null). */
  audit_count: z.number().int().min(0),
  /**
   * For `percentile = 'raw'` only — the audit uuid of the single sample.
   * Click-through from the chart resolves to /audits/{snapshot_uuid}.
   * `undefined` for aggregated series.
   */
  audit_uuid: z.string().optional(),
});

export type ExplorePoint = z.infer<typeof ExplorePointSchema>;

export const ExploreSeriesResultSchema = z.object({
  id: z.string(),
  /** Auto-derived label including site/page/device/region/percentile. */
  label: z.string(),
  unit: z.enum(['ms', 'score', 'unitless']),
  metric: ExploreMetricKeySchema,
  points: z.array(ExplorePointSchema),
});

export type ExploreSeriesResult = z.infer<typeof ExploreSeriesResultSchema>;

export const ExploreWarningSchema = z.object({
  series_id: z.string().optional(),
  code: z.enum(['LOW_SAMPLE_COUNT', 'TRUNCATED']),
  message: z.string(),
});

export type ExploreWarning = z.infer<typeof ExploreWarningSchema>;

export const ExploreResponseSchema = z.object({
  series: z.array(ExploreSeriesResultSchema),
  warnings: z.array(ExploreWarningSchema),
});

export type ExploreResponse = z.infer<typeof ExploreResponseSchema>;

// ─── Guardrails (shared between client + server) ────────────────────────────

/** Range → seconds. Shared by client (URL codec) + server (pipe binding). */
export const RANGE_SECONDS: Record<Range, number> = {
  '1h': 3600,
  '24h': 86_400,
  '7d': 7 * 86_400,
  '30d': 30 * 86_400,
  '90d': 90 * 86_400,
};

/** Bucket → seconds. Same shape, same callers. */
export const BUCKET_SECONDS: Record<Bucket, number> = {
  '5m': 300,
  '15m': 900,
  '1h': 3600,
  '6h': 21_600,
  '1d': 86_400,
  '7d': 7 * 86_400,
};

/**
 * Translate the user-facing percentile enum into the ClickHouse-side
 * argument the pipe expects. `avg` uses the sentinel `0` (no real
 * percentile would be exactly 0); the pipe branches on it.
 *
 * Throws for `raw` — callers must dispatch to the raw pipe instead.
 */
export function percentileToFloat(p: Percentile): number {
  switch (p) {
    case 'p50':
      return 0.5;
    case 'p75':
      return 0.75;
    case 'p95':
      return 0.95;
    case 'p99':
      return 0.99;
    case 'avg':
      return 0;
    case 'raw':
      throw new Error("percentileToFloat() called with 'raw' — dispatch to the raw pipe instead");
  }
}

/**
 * Approximate point count per series for a given range+bucket combination.
 * Used to reject configurations that would render unusable charts before
 * any SQL fires.
 */
export function approximatePointsPerSeries(range: Range, bucket: Bucket): number {
  return Math.ceil(RANGE_SECONDS[range] / BUCKET_SECONDS[bucket]);
}

/**
 * Validate the (range, bucket, percentile) triple against the guardrails.
 * Returns null on success or a human-readable error string on failure.
 *
 * Pure function — called from both Zod refinements and the dashboard's
 * inline preflight check.
 *
 * Replaces the older generic-point-cap rule. The (range, bucket) matrix
 * is now an explicit allow-list per ALLOWED_BUCKETS_BY_RANGE; combos
 * outside it return a 400 with the table reproduced in the message so
 * URL-paste failures are self-explanatory.
 */
export function validateExploreShape(input: ExploreRequest): string | null {
  const hasRaw = input.series.some((s) => s.percentile === 'raw');
  if (hasRaw) {
    // `raw` returns one row per audit. The bucket parameter is ignored
    // (we still require it in the request for shape uniformity but tell
    // the user it isn't doing anything).
    if (input.range === '90d') {
      return `'raw' percentile is only allowed when range <= 30d (got ${input.range})`;
    }
    // raw bypasses bucket entirely — no matrix check needed.
    return null;
  }
  if (!isBucketAllowedFor(input.range, input.bucket)) {
    const allowed = ALLOWED_BUCKETS_BY_RANGE[input.range].join(', ');
    return `bucket=${input.bucket} isn't allowed for range=${input.range}. Allowed buckets for ${input.range}: ${allowed}.`;
  }
  return null;
}
